import { useEffect, useState } from "react";
import moment from "moment";
import DashboardService from "../../services/dashboard.service";
import AccessoryService from "../../services/accessory.service";

const METRICS = [
  ["guest_guide_views", (start, end) => DashboardService.dashboardCountsGuestGuideViews(start, end)],
  ["survey_responses", (start, end) => DashboardService.dashboardCountsSurveyResponses(start, end)],
  ["extend_stays_messages_sent", (start, end) => DashboardService.dashboardCountsExtendStaysMessagesSent(start, end)],
  ["area_guides", () => DashboardService.dashboardCountsAreaGuides()],
  ["scheduled_messages", () => DashboardService.dashboardCountsScheduledMessages()],
  ["extras", async () => {
    const response = await AccessoryService.retrieve();
    return { data: { extras: Array.isArray(response?.data) ? response.data.length : undefined } };
  }],
];

export const getUsage = (feature, metrics = {}) => {
  const count = metrics[feature.metric];
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) {
    return { state: "unknown", label: "Not tracked yet", detail: "Usage information is not available for this feature." };
  }
  if (feature.usageBasis === "current") {
    return count > 0
      ? { state: "active", label: "Your team uses this", detail: `${count.toLocaleString()} ${count === 1 ? feature.metricSingular : feature.metricLabel} in your account.` }
      : { state: "inactive", label: `No ${feature.metricLabel} yet`, detail: feature.emptyUsageDetail };
  }
  if (count > 0) {
    return { state: "active", label: "Your team uses this", detail: `${count.toLocaleString()} ${feature.metricLabel} in the last 12 months.` };
  }
  return { state: "inactive", label: "No recent activity", detail: `No ${feature.metricLabel} recorded in the last 12 months. This does not mean your team has never used it.` };
};

export const useFeatureUsage = (accountId) => {
  const [result, setResult] = useState({ accountId: null, metrics: {}, loading: true, failed: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!accountId) return;
    let active = true;
    const now = moment();
    const end = now.format("YYYY-MM-DD HH:mm:ss");
    const start = now.clone().subtract(12, "months").format("YYYY-MM-DD HH:mm:ss");
    setResult({ accountId, metrics: {}, loading: true, failed: false });
    Promise.allSettled(METRICS.map(([, load]) => load(start, end))).then((responses) => {
      if (!active) return;
      const metrics = {};
      responses.forEach((response, index) => {
        const key = METRICS[index][0];
        const raw = response.status === "fulfilled" ? response.value?.data?.[key] : undefined;
        if ((typeof raw === "number" || (typeof raw === "string" && raw.trim() !== "")) && Number.isFinite(Number(raw)) && Number(raw) >= 0) {
          metrics[key] = Number(raw);
        }
      });
      setResult({ accountId, metrics, loading: false, failed: Object.keys(metrics).length !== METRICS.length });
    });
    return () => { active = false; };
  }, [accountId, attempt]);
  return {
    ...(result.accountId === accountId ? result : { metrics: {}, loading: !!accountId, failed: false }),
    retry: () => setAttempt((value) => value + 1),
  };
};
